import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchAgents, 
  selectAgents, 
  selectAgentLoading, 
  selectAgentError, 
  selectAgentPagination 
} from '../redux/slices/agentSlice';

const OurAgents = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const agents = useSelector(selectAgents);
  const loading = useSelector(selectAgentLoading);
  const error = useSelector(selectAgentError);
  const pagination = useSelector(selectAgentPagination);
  
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    specialization: searchParams.get('specialization') || '',
    experience: searchParams.get('experience') || '',
    sortBy: searchParams.get('sortBy') || 'name-asc',
    city: searchParams.get('city') || '',
    page: searchParams.get('page') || '1'
  });

  useEffect(() => {
    dispatch(fetchAgents(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: '1' };
    setFilters(newFilters);
    updateSearchParams(newFilters);
  };

  const updateSearchParams = (newFilters) => {
    const params = new URLSearchParams();
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) params.set(key, newFilters[key]);
    });
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const newFilters = { ...filters, page: page.toString() };
    setFilters(newFilters);
    updateSearchParams(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    const newFilters = { search: '', specialization: '', experience: '', sortBy: 'name-asc', city: '', page: '1' };
    setFilters(newFilters);
    setSearchParams(new URLSearchParams());
  };

  const AgentCard = ({ agent }) => (
    <div className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-gray-100">
      {/* Agent Photo */}
      <div className="relative overflow-hidden h-80">
        <img 
          src={agent.image 
            ? `${process.env.REACT_APP_API_URL}/uploads/${agent.image}`
            : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80"
          }
          alt={agent.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Online Status */}
        <div className="absolute top-4 right-4">
          <div className="flex items-center space-x-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            <span>Available</span>
          </div>
        </div>

        {/* Top Agent Badge */}
        {agent.isTopAgent && (
          <div className="absolute top-4 left-4">
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
              ⭐ TOP AGENT
            </span>
          </div>
        )}

        {/* Contact Overlay */}
        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex space-x-2">
            <a
              href={`tel:${agent.phone}`}
              className="flex-1 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white text-center py-2 rounded-lg font-semibold transition-all duration-300"
            >
              📞 Call
            </a>
            <a
              href={`mailto:${agent.email}`}
              className="flex-1 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white text-center py-2 rounded-lg font-semibold transition-all duration-300"
            >
              ✉️ Email
            </a>
          </div>
        </div>
      </div>
      
      {/* Agent Info */}
      <div className="p-8">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
            {agent.name}
          </h3>
          <p className="text-blue-600 font-semibold text-lg mb-3">{agent.specialization}</p>
          <p className="text-gray-600 leading-relaxed line-clamp-3">{agent.bio}</p>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl">
            <div className="text-3xl font-bold text-blue-600 mb-1">{agent.experience}+</div>
            <div className="text-sm text-blue-700 font-medium uppercase tracking-wide">Years</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
            <div className="text-3xl font-bold text-green-600 mb-1">{agent.propertiesSold}</div>
            <div className="text-sm text-green-700 font-medium uppercase tracking-wide">Sold</div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-center space-x-3 p-3 bg-gray-50 rounded-xl">
            <span className="text-blue-500 text-lg">📞</span>
            <span className="font-semibold text-gray-900">{agent.phone}</span>
          </div>
          <div className="flex items-center justify-center space-x-3 p-3 bg-gray-50 rounded-xl">
            <span className="text-blue-500 text-lg">✉️</span>
            <span className="font-semibold text-gray-900">{agent.email}</span>
          </div>
          {agent.city && (
            <div className="flex items-center justify-center space-x-3 p-3 bg-gray-50 rounded-xl">
              <span className="text-blue-500 text-lg">📍</span>
              <span className="font-semibold text-gray-900">{agent.city}</span>
            </div>
          )}
        </div>

        {/* Social Links */}
        {agent.socialLinks && Object.keys(agent.socialLinks).length > 0 && (
          <div className="flex justify-center space-x-3 mb-6">
            {Object.entries(agent.socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <span className="text-lg">
                  {platform === 'linkedin' ? '💼' : 
                   platform === 'instagram' ? '📷' : 
                   platform === 'facebook' ? '📘' : 
                   platform === 'twitter' ? '🐦' : '🔗'}
                </span>
              </a>
            ))}
          </div>
        )}

        
      </div>
    </div>
  );

  const AgentListItem = ({ agent }) => (
    <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex border border-gray-100">
      {/* Agent Photo */}
      <div className="relative w-64 flex-shrink-0 overflow-hidden">
        <img 
          src={agent.image 
            ? `${process.env.REACT_APP_API_URL}/uploads/${agent.image}`
            : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80"
          }
          alt={agent.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        
        {/* Status Badges */}
        <div className="absolute top-4 left-4">
          <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
            Available
          </div>
        </div>

        {agent.isTopAgent && (
          <div className="absolute top-4 right-4">
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
              ⭐ TOP AGENT
            </span>
          </div>
        )}
      </div>
      
      {/* Agent Details */}
      <div className="flex-1 p-8 flex flex-col justify-between">
        <div>
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                {agent.name}
              </h3>
              <p className="text-blue-600 font-semibold text-lg mb-3">{agent.specialization}</p>
              <p className="text-gray-600 leading-relaxed mb-4">{agent.bio}</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{agent.experience}+</div>
              <div className="text-xs text-blue-700 uppercase tracking-wide">Years</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{agent.propertiesSold}</div>
              <div className="text-xs text-green-700 uppercase tracking-wide">Sold</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{agent.averagePrice || 'N/A'}</div>
              <div className="text-xs text-purple-700 uppercase tracking-wide">Avg Price</div>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{agent.rating || '5.0'}</div>
              <div className="text-xs text-orange-700 uppercase tracking-wide">Rating</div>
            </div>
          </div>
        </div>

        {/* Contact Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <a
              href={`tel:${agent.phone}`}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-semibold"
            >
              <span>📞</span>
              <span>{agent.phone}</span>
            </a>
            <a
              href={`mailto:${agent.email}`}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-semibold"
            >
              <span>✉️</span>
              <span>Email</span>
            </a>
          </div>
          
          <Link
            to={`/agents/${agent.id}`}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1"
          >
            View Profile →
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Enhanced Header Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Meet Our <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Expert Agents</span>
          </h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto leading-relaxed mb-8">
            Work with our team of {agents.length || 0} experienced real estate professionals who are dedicated to helping you find your perfect home
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">{agents.length || 0}+</div>
              <div className="text-sm opacity-80">Expert Agents</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">1000+</div>
              <div className="text-sm opacity-80">Properties Sold</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">15+</div>
              <div className="text-sm opacity-80">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">98%</div>
              <div className="text-sm opacity-80">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Enhanced Filters Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-2xl shadow-xl p-8 sticky top-24">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold text-gray-900">Find Agents</h3>
                <button 
                  onClick={clearFilters}
                  className="text-red-600 hover:text-red-700 font-semibold text-sm bg-red-50 hover:bg-red-100 px-4 py-2 rounded-full transition-all duration-300"
                >
                  Clear All
                </button>
              </div>

              <div className="space-y-6">
                {/* Search */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    🔍 Search Agents
                  </label>
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                  />
                </div>

                {/* Specialization */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    🎯 Specialization
                  </label>
                  <select
                    value={filters.specialization}
                    onChange={(e) => handleFilterChange('specialization', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                  >
                    <option value="">All Specializations</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="luxury">Luxury Properties</option>
                    <option value="investment">Investment Properties</option>
                    <option value="first-time-buyers">First-time Buyers</option>
                    <option value="relocation">Relocation</option>
                  </select>
                </div>

                {/* Experience */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    📈 Experience Level
                  </label>
                  <select
                    value={filters.experience}
                    onChange={(e) => handleFilterChange('experience', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                  >
                    <option value="">Any Experience</option>
                    <option value="1-5">1-5 Years</option>
                    <option value="5-10">5-10 Years</option>
                    <option value="10-15">10-15 Years</option>
                    <option value="15+">15+ Years</option>
                  </select>
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    📍 City/Area
                  </label>
                  <input
                    type="text"
                    placeholder="Enter city or area"
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                  />
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    📊 Sort By
                  </label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                  >
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="experience-desc">Most Experience</option>
                    <option value="experience-asc">Least Experience</option>
                    <option value="sales-desc">Most Sales</option>
                    <option value="rating-desc">Highest Rated</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Agents Grid */}
          <div className="lg:w-3/4">
            {/* Results Header */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {agents.length || 0} Agents Found
                  </h2>
                  {filters.specialization && (
                    <p className="text-gray-600">
                      Specializing in <span className="font-semibold text-blue-600">{filters.specialization}</span>
                    </p>
                  )}
                  {error && (
                    <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg mt-2">❌ {error}</p>
                  )}
                </div>
                
                <div className="flex items-center space-x-4">
                  {/* View Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-all duration-300 ${
                        viewMode === 'grid' 
                          ? 'bg-white shadow-md text-blue-600' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-all duration-300 ${
                        viewMode === 'list' 
                          ? 'bg-white shadow-md text-blue-600' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </button>
                  </div>

                  {/* Pagination Info */}
                  <div className="text-sm text-gray-600">
                    Page {pagination.currentPage || 1} of {pagination.totalPages || 1}
                  </div>
                </div>
              </div>
            </div>

            {/* Agents Display */}
            {loading ? (
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8' : 'space-y-6'}>
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="animate-pulse bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="h-80 bg-gray-300"></div>
                    <div className="p-8">
                      <div className="h-6 bg-gray-300 rounded mb-4"></div>
                      <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                      <div className="h-12 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : agents.length > 0 ? (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {agents.map((agent) => (
                      <AgentCard key={agent.id} agent={agent} />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {agents.map((agent) => (
                      <AgentListItem key={agent.id} agent={agent} />
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-16 p-8 bg-white rounded-2xl shadow-lg">
                    <div className="text-gray-600 mb-4 sm:mb-0">
                      Showing {((pagination.currentPage - 1) * (pagination.limit || 12)) + 1} to {Math.min(pagination.currentPage * (pagination.limit || 12), pagination.totalAgents)} of {pagination.totalAgents} agents
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrev}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium rounded-lg transition-all duration-300"
                      >
                        Previous
                      </button>
                      
                      {[...Array(Math.min(pagination.totalPages, 5))].map((_, index) => {
                        const page = Math.max(1, Math.min(pagination.currentPage - 2, pagination.totalPages - 4)) + index;
                        if (page > pagination.totalPages) return null;
                        
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-4 py-2 font-medium rounded-lg transition-all duration-300 ${
                              page === pagination.currentPage
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNext}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium rounded-lg transition-all duration-300"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
                <div className="text-8xl mb-6">👨‍💼</div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">No Agents Found</h3>
                <p className="text-xl text-gray-600 mb-8">Try adjusting your search criteria or check back later</p>
                <button 
                  onClick={clearFilters}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl"
                >
                  Clear Filters & View All Agents
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Work with an <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Expert?</span>
          </h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto opacity-90 leading-relaxed">
            Our agents are here to help you every step of the way. Contact us today to get started on your real estate journey.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to="/contact"
              className="group relative inline-flex items-center justify-center px-12 py-5 bg-white text-blue-900 hover:bg-gray-100 font-bold text-lg rounded-2xl transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl overflow-hidden"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>Get Matched with an Agent</span>
              </span>
            </Link>
            <Link 
              to="/properties"
              className="group relative inline-flex items-center justify-center px-12 py-5 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold text-lg rounded-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span>Browse Properties</span>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurAgents;
